import React, { useEffect, useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Autocomplete,
  Button,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { PreLoader } from "../../subcomponents/Preloader";
import { textStyle } from "../../styles/Style";
import "../../../components/styles/popupStyle.css";
import {
  checkData,
  checkDuplicate,
  deleteItem,
  fillAutocompleteValues,
  updateData,
} from "../../actions/actionItems";
import InfoIcon from "@mui/icons-material/Info";
import { buildingCodesTooltip } from "../../../constants/TooltipInformation";
import DraggableItemList from "../../subcomponents/DNDComponent";
import AlertComponent from "../../subcomponents/AlertComponent";
import {
  validateString,
  validationLength,
  validationType,
} from "../../actions/validations";
import {
  versaFieldErrors,
  versaFieldHelperText,
} from "../../../constants/VersaHelperTexts";
import { tooltipComponent } from "../../subcomponents/tooltipComponent";
import { RoomPropertyEnum } from "../../../constants/NavbarList";
import { buttonValues, DNDFieldsEnum } from "../../../constants/Types";
import { ClearButton } from "../../subcomponents/ClearButton";
import { CopyButton } from "../../subcomponents/CopyButton";
import useScrollToTop from "../../hooks/useScrollToTop";

const BuildingCodes = ({
  data,
  fieldValues,
}: {
  data: any;
  fieldValues: any;
}) => {
  const [buildingCode, setBuildingCode] = useState<string | null>(null);
  const [buildingCodeError, setBuildingCodeError] = useState<boolean>(false);
  const [buildingName, setBuildingName] = useState<string | null>(null);
  const [buildingNameError, setBuildingNameError] = useState<boolean>(false);
  const [buildingInformation, setBuildingInformation] = useState<any>(null);
  const [taxExemptCode, setTaxExemptCode] = useState<string | null>(null);
  const [taxExemptList, setTaxExemptList] = useState<string[]>([]);
  const [active, setActive] = useState<boolean>(true);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [openAlert, setOpenAlert] = useState<boolean>(false);
  const [alertType, setAlertType] = useState<"success" | "error">("success");
  const [editClicked, setEditClicked] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [lastUpdatedRecord, setLastUpdatedRecord] = useState<any>(null);
  const { topRef, scrollToTop } = useScrollToTop();

  const buildingPayload = {
    code: buildingCode,
    name: buildingName,
    taxExemptCategories: taxExemptCode,
    isActive: active,
  };

  useEffect(() => {
    data(buildingInformation);
    setSelectedId(
      checkData(buildingInformation, selectedId) ? selectedId : null,
    );
  }, [buildingInformation]);

  useEffect(() => {
    if (fieldValues) {
      setBuildingInformation(
        Array.isArray(fieldValues[RoomPropertyEnum.buildingCodes])
          ? fieldValues[RoomPropertyEnum.buildingCodes]
          : fieldValues[RoomPropertyEnum.buildingCodes]
            ? [fieldValues[RoomPropertyEnum.buildingCodes]]
            : null,
      );
      setTaxExemptList(
        fillAutocompleteValues(
          fieldValues[RoomPropertyEnum.taxExemptCategory],
          DNDFieldsEnum.BUILDINGCODE,
          DNDFieldsEnum.BUILDINGNAME,
        ),
      );
    }
  }, []);

  useEffect(() => {
    validateInput();
  }, [buildingCode, buildingName]);

  const addBuilding = () => {
    if (validateInput()) {
      if (!buildingCode || buildingCode === null || buildingCode === "") {
        setBuildingCodeError(true);
      }
      if (!buildingName || buildingName === null || buildingName === "") {
        setBuildingNameError(true);
      } else if (
        checkDuplicate(
          Array.isArray(buildingInformation) ? buildingInformation : [],
          DNDFieldsEnum.BUILDINGCODE,
          buildingCode,
        )
      ) {
        setAlertMessage("Cannot have duplicate entries");
        setAlertType("error");
        setOpenAlert(true);
      } else {
        setBuildingInformation((prev: any) => {
          return updateData(
            Array.isArray(prev)
              ? [
                  ...prev,
                  {
                    id: prev.length + 1,
                    ...buildingPayload,
                  },
                ]
              : [
                  {
                    id: 1,
                    ...buildingPayload,
                  },
                ],
          );
        });
        setLastUpdatedRecord(buildingPayload);
        editBuilding(null);
        setEditClicked(false);
      }
    }
  };

  const validateInput = () => {
    setBuildingCodeError(false);
    setBuildingNameError(false);
    let isValid = true;
    if (
      buildingCode &&
      !validateString(
        buildingCode,
        validationLength.buildingCode,
        validationType.CODE,
      )
    ) {
      setBuildingCodeError(true);
      isValid = false;
    }
    if (
      buildingName &&
      !validateString(
        buildingName,
        validationLength.buildingName,
        validationType.OTHER,
      )
    ) {
      setBuildingNameError(true);
      isValid = false;
    }
    return isValid;
  };

  const editBuilding = (list: any) => {
    list?.id && setSelectedId(list.id);
    list !== null && setEditClicked(true);
    setBuildingCode(list?.code);
    setBuildingName(list?.name);
    setTaxExemptCode(list?.taxExemptCategories);
    setActive(list ? list.isActive : true);
  };

  const updateBuilding = () => {
    if (!validateInput()) return;
    if (!buildingCode || buildingCode === null || buildingCode === "") {
      setBuildingCodeError(true);
      return;
    }
    if (!buildingName || buildingName === null || buildingName === "") {
      setBuildingNameError(true);
      return;
    }
    if (
      checkDuplicate(
        (Array.isArray(buildingInformation) ? buildingInformation : []).filter(
          (item: any) => String(item.id) !== String(selectedId),
        ),
        DNDFieldsEnum.BUILDINGCODE,
        buildingCode,
      )
    ) {
      setAlertMessage("Cannot have duplicate entries");
      setAlertType("error");
      setOpenAlert(true);
      return;
    }
    setBuildingInformation((prev: any) =>
      Array.isArray(prev)
        ? prev.map((item: any) => {
            if (item.id === selectedId) {
              return {
                ...item,
                ...buildingPayload,
              };
            }
            return item;
          })
        : prev,
    );
    editBuilding(null);
    setEditClicked(false);
    setSelectedId(null);
    setAlertMessage(`${RoomPropertyEnum.buildingCodes} updated successfully`);
    setAlertType("success");
    setOpenAlert(true);
  };

  const deleteBuilding = (id: number) => {
    setBuildingInformation(deleteItem(buildingInformation, id));
  };

  const buildingCodeHelperText = buildingCodeError
    ? !(buildingCode?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.buildingCodes.buildingCode
    : versaFieldHelperText.buildingCodes.buildingCode;

  const buildingNameHelperText = buildingNameError
    ? !(buildingName?.trim() ?? "")
      ? versaFieldErrors.required
      : versaFieldErrors.buildingCodes.buildingName
    : versaFieldHelperText.buildingCodes.buildingName;

  return (
    <Stack direction="column" gap="10px">
      <div ref={topRef} />
      <Stack direction="column" width="100%">
        <Paper
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "20px",
          }}
        >
          <Stack
            direction="row"
            width="100%"
            gap="10px"
            justifyContent="space-between"
            alignItems="center"
            mb={4}
          >
            <Typography variant="h6" component="div" sx={textStyle}>
              Building Information
              <Tooltip title={tooltipComponent(buildingCodesTooltip.header)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </Typography>
            <Stack direction="row" gap="10px" justifyContent="space-between">
              {editClicked && (
                <ClearButton
                  click={() => {
                    editBuilding(null);
                    setSelectedId(null);
                  }}
                />
              )}
              {lastUpdatedRecord && (
                <CopyButton
                  click={() => {
                    editBuilding(lastUpdatedRecord);
                  }}
                />
              )}
            </Stack>
          </Stack>
          <Stack
            className="form-two-col-row"
            direction="row"
            gap="20px"
            width="100%"
          >
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Building Code
                <Tooltip
                  title={tooltipComponent(buildingCodesTooltip.buildingCode)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="buildingCode"
                variant="outlined"
                placeholder="Building Code"
                helperText={buildingCodeHelperText}
                value={buildingCode ?? ""}
                error={buildingCodeError}
                sx={{ width: "100%" }}
                onChange={(e: any) => {
                  setBuildingCode(e.target.value.toUpperCase());
                  setBuildingCodeError(false);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Building Name
                <Tooltip
                  title={tooltipComponent(buildingCodesTooltip.buildingName)}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <TextField
                id="buildingName"
                variant="outlined"
                placeholder="Building Name"
                helperText={buildingNameHelperText}
                value={buildingName ?? ""}
                error={buildingNameError}
                sx={{ width: "100%" }}
                onChange={(e: any) => {
                  setBuildingName(e.target.value);
                  setBuildingNameError(false);
                }}
              />
            </Stack>
            <Stack direction="column" width="100%" gap="5px">
              <FormLabel component="legend">
                Tax Exempt Category
                <Tooltip
                  title={tooltipComponent(
                    buildingCodesTooltip.taxExemptCategory,
                  )}
                >
                  <InfoIcon fontSize="small" />
                </Tooltip>
              </FormLabel>
              <Autocomplete
                id="taxExemptCodeDropDown"
                disablePortal
                options={taxExemptList}
                sx={{ width: "100%" }}
                value={taxExemptCode ?? ""}
                disabled={!taxExemptList}
                onChange={(e: any) => setTaxExemptCode(e.target.innerText)}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Tax Exempt Category" />
                )}
              />
            </Stack>
          </Stack>
          <Stack direction="column" gap="5px" width="max-content">
            <FormLabel component="legend">
              Active?
              <Tooltip title={tooltipComponent(buildingCodesTooltip.active)}>
                <InfoIcon fontSize="small" />
              </Tooltip>
            </FormLabel>
            <FormControlLabel
              control={
                <Switch checked={active} onChange={() => setActive(!active)} />
              }
              label=""
            />
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end">
            <Button
              variant="outlined"
              sx={{
                cursor: "pointer",
                border: "1px solid gray",
                borderRadius: "10px",
                background: "darkgreen",
                color: "white",
              }}
              onClick={selectedId ? updateBuilding : addBuilding}
              id="addNewBuilding"
            >
              {selectedId ? buttonValues.UPDATE : buttonValues.ADD} New Building
            </Button>
          </Stack>
          <Stack direction="column" gap="10px" mt={4}>
            <Typography variant="h6">Buildings Available</Typography>
            <Typography variant="body2" sx={{ color: "red", fontWeight: 800 }}>
              Do not use sample data. Those are for demo purposes only!
            </Typography>
            <Stack direction="column" gap="10px">
              {buildingInformation ? (
                <DraggableItemList
                  itemData={buildingInformation}
                  onOrderUpdate={(order) => setBuildingInformation(order)}
                  onEdit={(info) => {
                    editBuilding(info);
                    scrollToTop();
                  }}
                  onDelete={(id) => deleteBuilding(id)}
                  code={DNDFieldsEnum.BUILDINGCODE}
                  name={DNDFieldsEnum.BUILDINGNAME}
                />
              ) : (
                <Typography variant="body2">
                  No Building Information Available
                </Typography>
              )}
            </Stack>
          </Stack>
        </Paper>
      </Stack>
      <AlertComponent
        message={alertMessage}
        type={alertType}
        alertOpen={openAlert}
        closeAlert={() => setOpenAlert(false)}
      />
    </Stack>
  );
};

export default BuildingCodes;
